import React, { useContext, useState, useRef } from "react";
import Box from "@material-ui/core/Box";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";

import { R } from "@utils/currency"
import { CardContent } from "@components/layout/Card";
import { Formik, Form as FormikForm, useFormikContext } from "formik";
import { NumberFieldWithLabel, CheckboxWithLabel } from "@components/Form/Fields";
import { Button } from "@components/Button";
import { FnaContext } from "../../../context";
import { ASSESSMENT_TYPE, FORM_ACTIONS } from "../../../reducer";
import { DependentsFields, sumDependents } from "./Dependents";
import { buildInitialValues } from "./initialValues";
import { setupValidationSchema } from "./validations";
import { sumCurrency } from "@utils/currency";
import { Spotlight } from "@components/Spotlight";
import { Tooltip } from "@components/Tooltip";
import { Aside } from '../../../Components/Form/Aside'
import { sumSelectedInvestmentPropertyLiabilities } from "../../../utils";
import { ASIDE_VIEWS } from "../../../reducer";

// TODO: Current form has no mobile designs
export const Form = () => {
  const {
    actions: { submit },
  } = useContext(FnaContext);
  const [asideView, setAsideView] = useState(false);
  return (
    <CardContent>
      <Formik
        onSubmit={(values) => submit(values, ASSESSMENT_TYPE.LIFE_COVER)}
        initialValues={buildInitialValues()}
        validationSchema={setupValidationSchema()}
        render={({ values }) => (
          <FormikForm>
            <FormSpotlights values={values} />
            <div className="grid grid-cols-2">
              <Main setAsideView={setAsideView} />
              <Aside asideView={asideView} />
            </div>
          </FormikForm>
        )}
      />
    </CardContent>
  );
};

export const Main = ({ setAsideView }) => {
  const { values, isValid, isSubmitting } = useFormikContext();
  const {
    state: { formAction, assets, assetables, liabilities, loading, riskProducts },
    actions: { goToIndex, goToDelete },
  } = useContext(FnaContext);

  const assetsExist = !isEmpty(assets)
  const liabilitiesExist = !isEmpty(liabilities)
  const riskProductsExist = !isEmpty(riskProducts)

  const newForm = formAction === FORM_ACTIONS.CREATE;

  // scroll to top for asset & liability selection

  const scrollToRef = (ref) => ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" })
  const topElementRef = useRef();

  const investmentPropertyLiabilityValue = sumSelectedInvestmentPropertyLiabilities(assets, values.assetables_attributes)
  return (
    <Box className="app-form fna-form" ref={topElementRef}>
      <h3>Things to pay for right now</h3>
      <Box mb="2rem">
        <h4>Liabilities</h4>
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          mr="1rem"
        >
          <NumberFieldWithLabel
            readOnly
            value={get(values, "total_from_selected_liabilities", 0)}
            style={{ flex: 2 }}
            label="Total from selected liabilities"
            name="total_from_selected_liabilities"
            subtext={investmentPropertyLiabilityValue && `Of which ${R(investmentPropertyLiabilityValue).format()} is from investment properties.`}
          />
          <Button
            trackAs="Life cover: Select liabilities"
            style={{ flex: 1 }}
            disabled={!liabilitiesExist}
            primary
            onClick={() => {
              scrollToRef(topElementRef);
              setAsideView(ASIDE_VIEWS.LIABILITIES);
            }}
          >
            Select liabilities
          </Button>
        </Box>
        {!liabilitiesExist && (
          <small style={{ marginLeft: "1rem" }}>
            You do not have any liabilities
          </small>
        )}
        <NumberFieldWithLabel
          label="Additional liabilities value"
          name="total_additional_liabilities"
        />
      </Box>
      <h3>Things to pay for over time</h3>
      <h4>Dependents</h4>
      <DependentsFields setAsideView={setAsideView} />
      <h3>Existing Provisions</h3>
      <Box mb="2rem">
        <h4>Assets</h4>
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          mr="1rem"
        >
          <NumberFieldWithLabel
            readOnly
            value={get(values, "total_from_selected_assets", 0)}
            style={{ flex: 2 }}
            label="Total from selected assets"
            name="total_from_selected_assets"
          />
          <Button
            trackAs="Life cover: Select assets"
            style={{ flex: 1 }}
            disabled={!assetsExist}
            primary
            onClick={() => {
              scrollToRef(topElementRef);
              setAsideView(ASIDE_VIEWS.ASSETS);
            }}
          >
            Select Assets
          </Button>
        </Box>
        {!assetsExist && (
          <small style={{ marginLeft: "1rem" }}>
            You do not have any assets
          </small>
        )}
        <NumberFieldWithLabel
          label="Additional asset value"
          name="total_additional_assets"
        />
        <CheckboxWithLabel
          name={'include_estate_duty_in_required_cover'}
          label={"Include estate duty as part of required cover"}
        />
        {
          <small style={{ marginLeft: "1rem" }}>
            <a onClick={() => setAsideView(ASIDE_VIEWS.ESTATE_DUTY)}>
              What about estate duty?
            </a>
          </small>
        }
      </Box>
      <Box mb="2rem">
        <h4>Existing Cover</h4>
        <Box
          display="flex"
          alignItems="flex-end"
          justifyContent="space-between"
          mr="1rem"
        >
          <NumberFieldWithLabel
            readOnly
            value={get(values, "total_from_selected_risk_products", 0)}
            style={{ flex: 2 }}
            label="Total from selected risk products"
            name="total_from_selected_risk_products"
          />
          <Button
            trackAs="Life cover: Select risk products"
            style={{ flex: 1 }}
            disabled={!riskProductsExist}
            primary
            onClick={() => {
              scrollToRef(topElementRef);
              setAsideView(ASIDE_VIEWS.RISK_PRODUCTS)
            }}
          >
            Select risk products
          </Button>
        </Box>
        {!riskProductsExist && (
          <small style={{ marginLeft: "1rem" }}>
            You do not have any risk products
          </small>
        )}
        <NumberFieldWithLabel
          label="Existing life cover"
          name="total_existing_life_cover"
        />
      </Box>
      <Box className="grid grid-cols-2 gap-4">
        <Button secondary onClick={goToIndex}>
          Go Back
        </Button>
        <Button
          trackAs="Life cover: Complete assessment"
          disabled={!isValid || isSubmitting || loading}
          primary
          type="submit"
        >
          {loading ? "Calculating..." : "Calculate"}
        </Button>
        {!newForm && (
          <Button
            disabled={loading}
            trackAs="Life cover: Delete"
            tertiary
            onClick={goToDelete(ASSESSMENT_TYPE.LIFE_COVER)}
            className="button col-span-2"
          >
            Delete assessment
          </Button>
        )}
      </Box>
    </Box>
  );
};

const FormSpotlights = ({ values }) => {
  return (
    <Box px="3rem" my="1rem" display="flex" justifyContent="space-between">
      <Spotlight
        value={sumCurrency([
          get(values, "total_additional_assets", 0),
          get(values, "total_from_selected_assets", 0),
          get(values, "total_from_selected_risk_products", 0)
        ]).format()}
        description={`Current cover plus assets`}
      />
      <Tooltip
        width={450}
        title={
          <>
            <p>
              The total required cover is calculated based on a combination of
              everything you have to pay for immediately, such as covering any
              debts you might have, and things you need to pay over time, such
              as a monthly payment to a dependent.
            </p>
            <p>
              If you've added dependents, it's important to note that we make an
              investment assumption for when the lump sum is paid out. We assume
              this lump sum is placed in an investment with a target return of
              your current risk profile.
            </p>
            <p style={{ marginBottom: 0 }}>
              We also include a range calculation, to show what the requirement
              might be if the money was invested differently, with the lower
              requirement value of the range assuming a return of CPI + 4%, and
              the higher requirement value assumes a return of CPI - 2%.
            </p>
          </>
        }
      >
        <Spotlight
          value={`${sumCurrency([
            get(values, "total_additional_liabilities", 0),
            get(values, "total_from_selected_liabilities", 0),
            sumDependents(values),
          ]).format()}*`}
          description={`Required cover`}
        />
      </Tooltip>
    </Box>
  );
};
